import React, { Component } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { EmvioLoadingSpinner } from '@nexio/emvio-util-ui';
import {
    EmvioCheckbox,
    EmvioValidatedButton,
    EmvioValidatedForm
} from '@nexio/emvio-form-components';
import { updateUser } from '../UserSettings/userSettingsActions';
import PropTypes from 'prop-types';
import { selfShape } from '../../utils/propTypes';
import {
    getIsLoading as getIsUserLoading,
    getSelf
} from '../Self/selfEntity';
import {
    getIsUpdateUserLoading,
    getIsUpdateUserSuccessful,
    getUpdateUserErrorMessage
} from '../UserSettings/userSettingsEntity';
import { doesPropExistAndHasItChanged } from '@nexio/emvio-util-app';
import { toast } from 'react-toastify';
import { toastIfError } from '../../utils/toast';
import './NotificationSettings.scss';
import config from '../../config/config';
import { doesRoleHaveAccessToFeature } from '../../utils/customRole';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

const categories = [
    {
        displayName: 'Merchant Status Changes',
        name: 'statusChanges',
        subcategories: [
            {
                displayName: 'Pre-Vet',
                name: 'preVet'
            },
            {
                displayName: 'App Created',
                name: 'applicationCreated'
            },
            {
                displayName: 'App Sent',
                name: 'applicationSent'
            },
            {
                displayName: 'App Received',
                name: 'applicationReceived'
            },
            {
                displayName: 'Underwriting',
                name: 'underwriting'
            },
            {
                displayName: 'Pending',
                name: 'pending'
            },
            {
                displayName: 'Underwritten',
                name: 'underwritten'
            },
            {
                displayName: 'Boarded',
                name: 'boarded'
            },
            {
                displayName: 'Live',
                name: 'live'
            },
            {
                displayName: 'Declined',
                name: 'declined'
            },
            {
                displayName: 'Inactive',
                name: 'inactive'
            },
            {
                displayName: 'Closed',
                name: 'closed'
            }
        ]
    },
    {
        displayName: 'Chargebacks',
        name: 'chargebacks'
    }, {
        displayName: 'Fraud',
        name: 'fraud'
    }, {
        displayName: 'Large Exports',
        name: 'largeExports'
    }, {
        displayName: 'Payout Batches',
        name: 'payoutBatches'
    }
];

const legacyCategories = [
    {
        displayName: 'Chargebacks',
        name: 'chargebacks'
    }, {
        displayName: 'Fraud',
        name: 'fraud'
    }, {
        displayName: 'Large Exports',
        name: 'largeExports'
    }, {
        displayName: 'Payout Batches',
        name: 'payoutBatches'
    }
];

export class NotificationSettings extends Component {
    static propTypes = {
        error: PropTypes.string,
        isUpdateUserLoading: PropTypes.bool,
        isUpdateUserSuccessful: PropTypes.bool,
        isUserLoading: PropTypes.bool,
        onUpdateUser: PropTypes.func.isRequired,
        user: selfShape
    };

    constructor(props) {
        super(props);

        this.renderNotificationCategory = this.renderNotificationCategory.bind(this);
        this.handleCheckNotificationCategory = this.handleCheckNotificationCategory.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.ensureNotificationsDefaultToOn = this.ensureNotificationsDefaultToOn.bind(this);
        this.initializeCategories = this.initializeCategories.bind(this);

        this.initializeCategories();

        this.state = {
            notifications: this.ensureNotificationsDefaultToOn(this.props.user),
            previousLDFLagValue: props.flags?.resellerPhase1,
            expandedCategories: []
        };
    }

    initializeCategories() {
        this.categories = _.cloneDeep(this.props.flags?.resellerPhase1 && this.props.user?.accountType === 'RESELLER' ? categories : legacyCategories);

        const { customAccess } = _.get(this.props, 'user') || {};

        if (customAccess) {
            const { serviceArns } = config;

            this.categories = this.categories.filter((category) => {
                switch (category.name) {
                    case 'chargebacks':
                        return doesRoleHaveAccessToFeature(customAccess, {
                            [serviceArns.chargeback]: ['GET/v3/{id}']
                        });
                    case 'fraud':
                        return doesRoleHaveAccessToFeature(customAccess, {
                            [serviceArns.transaction]: ['GET/v3', 'GET/v3/count']
                        }, true);

                    case 'largeExports':
                        return doesRoleHaveAccessToFeature(customAccess, {
                            [serviceArns.exports]: ['GET/v3/getExports']
                        });
                    case 'payoutBatches':
                        return doesRoleHaveAccessToFeature(customAccess, {
                            [serviceArns.payout]: ['GET/v3/batch/{payoutBatchId}']
                        });
                    default:
                        return true;
                }
            });
        }

        const allCategories = this.props.flags?.resellerPhase1 ? categories : legacyCategories;
        const flattenedCategories = allCategories.map((category) => {
            if (category.subcategories) {
                return category.subcategories.map((subcategory) => subcategory);
            }

            return category;
        });

        this.defaultNotifications = flattenedCategories.reduce((notificationsObject, category) => {
            _.get(this.props.user, 'notifications') ? notificationsObject[category.name] = true : notificationsObject[category.name] = false;

            return notificationsObject;
        }, {});
    }

    ensureNotificationsDefaultToOn(user = {}) {
        let notifications = {
            ...this.defaultNotifications,
            ...user.notifications
        };

        return notifications;
    }

    componentDidUpdate(prevProps) {
        toastIfError(prevProps, this.props);

        const currentFlagValue = this.props.flags?.resellerPhase1;

        if (this.state.previousLDFLagValue !== currentFlagValue) {
            this.initializeCategories();
            this.setState({
                notifications: this.ensureNotificationsDefaultToOn(this.props.user),
                previousLDFLagValue: currentFlagValue
            });
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'user')) {
            this.setState({
                notifications: this.ensureNotificationsDefaultToOn(this.props.user)
            });
        }

        if (doesPropExistAndHasItChanged(prevProps, this.props, 'isUpdateUserSuccessful') && this.props.isUpdateUserSuccessful === true) {
            toast('User updated successfully!', { type: 'success' });
        }
    }

    handleCheckNotificationCategory(name, shouldCloseDropdown, shouldOpenDropdown) {
        return () => {
            let newExpandedCategories = [...this.state.expandedCategories];
            if (shouldCloseDropdown) {
                newExpandedCategories = this.state.expandedCategories.filter((category) => category !== name);
            } else if (shouldOpenDropdown) {
                newExpandedCategories = [...this.state.expandedCategories, name];
            }

            this.setState({
                notifications: {
                    ...this.state.notifications,
                    [name]: !this.state.notifications[name]
                },
                expandedCategories: newExpandedCategories
            });
        };
    }

    renderNotificationCategory({ displayName, name, subcategories = null, disabled = false }) {
        const handleToggleDropdown = (toggleCategory) => {
            if (this.state.expandedCategories.includes(toggleCategory)) {
                this.setState({ expandedCategories: this.state.expandedCategories.filter((category) => (category !== toggleCategory)) });
            } else {
                this.setState({ expandedCategories: [toggleCategory, ...this.state.expandedCategories] });
            }
        };

        const isChecked = this.state.notifications[name];

        return (
            // This specific key triggers a re-render when 'disabled' prop updates
            <React.Fragment key={`${name}-${disabled}`}>
                <div className="notification-category">
                    <EmvioCheckbox
                        checked={isChecked}
                        onChange={this.handleCheckNotificationCategory(name, subcategories && isChecked, subcategories && !isChecked)}
                        onEnter={this.handleSubmit}
                        disabled={disabled}
                    />

                    <div className="display-name">{displayName}</div>

                    {
                        subcategories && (
                            this.state.expandedCategories.includes(name) ?
                                <span className={`icon-caret-down${isChecked ? '' : ' hidden'}`} onClick={() => handleToggleDropdown(name)} /> :
                                <span className={`icon-caret-down is-collapsed${isChecked ? '' : ' hidden'}`} onClick={() => handleToggleDropdown(name)} />
                        )
                    }
                </div>

                {subcategories && this.state.expandedCategories.includes(name) && (
                    <div className="subcategories">
                        {subcategories.map((category) => this.renderNotificationCategory({ ...category, disabled: !isChecked }))}
                    </div>
                )}
            </ React.Fragment>
        );
    }

    handleSubmit() {
        const updatedUserNotifications = _.mapValues(this.state.notifications, ((shouldNotify, notification) => {
            if (this.props.user.customAccess) {
                return !!_.find(this.categories, { name: notification }) && shouldNotify;
            }

            return shouldNotify;
        }));

        this.props.onUpdateUser({
            ...this.props.user,
            notifications: updatedUserNotifications
        });
    }

    render() {
        if (!this.props.user) {
            return <div className="emvio-notification-settings no-access">
                Unable to retrieve notification settings.
            </div>;
        }

        if (_.isEmpty(this.categories)) {
            return <div className="emvio-notification-settings no-access">
                Insufficient access rights to receive notifications.
            </div>;
        }

        if (_.isEmpty(this.props.user.payoutAccessRights)) {
            _.remove(this.categories, (category) => {
                return category.name === 'payoutBatches';
            });
        }

        return (<EmvioValidatedForm
            className="emvio-notification-settings"
            onSubmit={this.handleSubmit}>
            <div className="title">
                E-mail notifications you are subscribed to:
            </div>
            {this.props.isUserLoading
                ? <EmvioLoadingSpinner
                    className="loading-spinner"
                    relative
                />
                : <div>
                    <div className="categories">
                        {this.categories.map(this.renderNotificationCategory)}
                    </div>
                    <EmvioValidatedButton
                        invertColors
                        isLoading={this.props.isUpdateUserLoading}
                        size="large">
                        Save
                    </EmvioValidatedButton>
                </div>
            }
        </EmvioValidatedForm>);
    }
}

function mapStateToProps(state) {
    return {
        error: getUpdateUserErrorMessage(state),
        isUpdateUserLoading: getIsUpdateUserLoading(state),
        isUpdateUserSuccessful: getIsUpdateUserSuccessful(state),
        isUserLoading: getIsUserLoading(state),
        user: getSelf(state)
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onUpdateUser: (user) => dispatch(updateUser(user))
    };
}

export default withLDConsumer()(connect(mapStateToProps, mapDispatchToProps)(NotificationSettings));
